import { useCallback, useContext, useEffect, useState } from 'react';
import { ImageType } from 'react-images-uploading';
import { Trash } from 'lucide-react';

import { ApiGetCompanyType } from '../../../api/types/apiGetCompany';
import { Context, Loader, TagsInput, TextField } from '../../../components';
import Button from '../../../components/Buttons/Button';
import { useWizard } from '../../../components/Wizard';
import cn from '../../../lib/classNames';
import { api } from '../../../sagas';
import Layout from '../../Layout/LayoutStarted';
import UploadFile from '../UploadFile';

import styles from './Step1.module.scss';

type Step1Type = {
	setCompanyInfo: (data: ApiGetCompanyType) => void;
};

export default function Step1(props: Step1Type) {
	const [nameValue, setNameValue] = useState('');
	const [businessUnitValue, setBusinessUnitValue] = useState<string[]>([]);
	const [emailDomainValue, setEmailDomainValue] = useState<string[]>([]);
	const [locationValue, setLocationValue] = useState('');
	const [isSubmitted, setIsSubmitted] = useState(false);

	const { nextStep } = useWizard();

	const [companyData, setCompanyData] = useState<ApiGetCompanyType | null>(null);
	const [isLoader, setIsLoader] = useState(true);

	const { userProfileData, setUserProfileData } = useContext(Context);

	const {
		name = '',
		emailDomains = [],
		businessUnits = [],
		location = '',
		logo = '',
	} = companyData || {};

	const getCompanyInfo = () => {
		api
			.getCompanyInfo()
			.then((response) => {
				if (response.ok) {
					setCompanyData(response.data);
					setIsLoader(false);
				}
			})
			.catch((err) => {
				console.error('*** getCompanyInfo > getCompanyInfo', err);
			});
	};

	useEffect(() => {
		getCompanyInfo();
	}, []);

	useEffect(() => {
		setCompanyData((state) => {
			if (state === null) return null;
			return {
				...state,
				name: nameValue,
				emailDomains: emailDomainValue,
				businessUnits: businessUnitValue,
				location: locationValue,
			};
		});
	}, [nameValue, emailDomainValue, businessUnitValue, locationValue]);

	const onSelect = (selectedFile: ImageType) => {
		if (selectedFile.length) {
			const formData = new FormData();
			formData.append('file', selectedFile[0]);
			api.uploadCompanyLogo(formData).then((response) => {
				if (response.ok) {
					setUserProfileData({
						...userProfileData,
						company: { ...(userProfileData.company || {}), logo: response.data.avatarId },
					});

					setCompanyData((state) => {
						if (state === null) return null;
						return {
							...state,
							logo: response.data.avatarId,
						};
					});
				}
			});
		}
	};

	const onDeleteLogo = () => {
		api.deleteCompanyLogo().then((response) => {
			if (response.ok) {
				setUserProfileData({
					...userProfileData,
					company: { ...(userProfileData.company || {}), logo: null },
				});

				setCompanyData((state) => {
					if (state === null) return null;
					return {
						...state,
						logo: null,
					};
				});
			}
		});
	};

	const reset = useCallback(() => {
		setBusinessUnitValue(businessUnits || []);
		setEmailDomainValue(emailDomains || []);
		setLocationValue(location || '');
		setNameValue(name || '');
	}, [companyData]);

	useEffect(() => {
		reset();
	}, [companyData]);

	const handleSubmit = useCallback(() => {
		setIsSubmitted(true);

		if (companyData === null) {
			return;
		}
		props.setCompanyInfo(companyData);
		nextStep();
	}, [companyData]);

	return (
		<Layout displayRightBlock={false} displayFooter>
			{isLoader && <Loader size="small" />}
			<div className={cn(styles['company-wrap'], { [styles.load]: isLoader })}>
				<div className={cn(styles['form-wrap'], styles.center)}>
					<div className={styles['title-wrap']}>
						<h3 className={styles.title}>A personal solution for your business</h3>
					</div>

					<h4 className={styles['sub-title']}>
						Please review your company details and upload your logo to personalize your experience.
					</h4>

					<div className={styles.body}>
						<div className={styles['field-inline']}>
							<div className={cn(styles['field-wrap'], styles['field-wrap-gender'])}>
								<div className={cn(styles['field-inner'], styles['field-required'])}>
									<TextField
										label="Company Name"
										placeholder="Company Name"
										value={nameValue}
										onChange={setNameValue}
									/>
									{isSubmitted && nameValue.length === 0 && (
										<p className={styles['field-error']}>Enter a company name</p>
									)}
								</div>
							</div>
						</div>

						<div className={styles['field-inline']}>
							<div className={cn(styles['field-wrap'])}>
								<div className={cn(styles['field-inner'], styles['field-required'])}>
									<TextField
										label="Company location"
										placeholder="Company location"
										value={locationValue}
										onChange={setLocationValue}
									/>
								</div>
							</div>
						</div>

						<div className={styles['field-inline']}>
							<div className={cn(styles['field-wrap'])}>
								<div className={styles['field-inner']}>
									<TagsInput
										value={businessUnitValue}
										onChange={setBusinessUnitValue}
										name="Business Unit"
										placeHolder="Business Unit"
									/>
								</div>
							</div>
						</div>

						<div className={styles['field-inline']}>
							<UploadFile maxFileSize={5} acceptType={['jpg', 'png', 'jpeg']} onSelect={onSelect} />
						</div>

						{logo && (
							<div className={styles['field-inline']}>
								<div className={styles.logo}>
									<div className={styles.wrap}>
										<img src={logo} alt="Company logo" />
										<span>Logo.png</span>
									</div>
									<a
										onClick={(e: any) => {
											e.stopPropagation();

											if (window.confirm('Are you sure you want to delete the logo?')) {
												onDeleteLogo();
											}
										}}
									>
										<Trash size={20} />
									</a>
								</div>
							</div>
						)}

						<div className={cn(styles['field-inline'], styles.buttons)}>
							<Button onClick={handleSubmit} variant="filled" color="primary" sizeWidth="full">
								NEXT
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
